import { useIsMobile } from '../hooks/useIsMobile';
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Button, TextField,
  Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, InputAdornment, FormControl, InputLabel,
  Select, MenuItem, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import CampaignIcon from '@mui/icons-material/Campaign';
import Sidebar from '../components/Sidebar';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SIDEBAR_WIDTH = 230;

const DEPARTMENTS = [
  'HR&A', 'Resource Mobilization and Grant Management',
  'Social Mobilization and Outreach', 'EQAS', 'EVT',
  'OOSC', 'PRS', 'FA', 'IA', 'PROTOCOL',
  'SERVICOM', 'PROCUREMENT', 'LEGAL', 'AE',
];

const STATES = [
  'Abia', 'Adamawa', 'Akwa-Ibom', 'Anambra', 'Bauchi', 'Bayelsa',
  'Benue', 'Cross-River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi',
  'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara',
];

export default function AnnouncementsPage() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isDeptHead = user?.role === 'department_head';
  const [announcements, setAnnouncements] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [openCreate, setOpenCreate] = useState(false);
  const [openView, setOpenView] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [form, setForm] = useState({
    title: '',
    body: '',
    expires_at: '',
    audience_type: isDeptHead ? 'department' : 'all',
    audience_filter: '',
  });
  const [createLoading, setCreateLoading] = useState(false);

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [announcements, search, filterStatus, filterDateFrom, filterDateTo]);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const endpoint = isAdmin ? '/announcements/all/' : '/announcements/';
      const { data } = await api.get(endpoint);
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch {
      setMsg({ type: 'error', text: 'Failed to load announcements.' });
    } finally {
      setLoading(false);
    }
  };

  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetchAnnouncements();
    if (isAdmin) {
      api.get('/users/').then(({ data }) => {
        setAllUsers(Array.isArray(data) ? data : data.results || []);
      });
    }
  }, []);

  const applyFilters = () => {
    let result = [...announcements];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(a =>
        a.title.toLowerCase().includes(q) ||
        a.body.toLowerCase().includes(q) ||
        a.created_by_name?.toLowerCase().includes(q)
      );
    }

    if (filterStatus === 'active') result = result.filter(a => a.is_active);
    if (filterStatus === 'inactive') result = result.filter(a => !a.is_active);
    if (filterStatus === 'read') result = result.filter(a => a.is_read_by_me);
    if (filterStatus === 'unread') result = result.filter(a => !a.is_read_by_me);

    if (filterDateFrom) {
      result = result.filter(a => new Date(a.created_at) >= new Date(filterDateFrom));
    }
    if (filterDateTo) {
      result = result.filter(a => new Date(a.created_at) <= new Date(filterDateTo + 'T23:59:59'));
    }

    setFiltered(result);
  };

  const handleCreate = async () => {
  if (!form.title.trim() || !form.body.trim()) {
    setMsg({ type: 'error', text: 'Title and body are required.' });
    return;
  }
  if (isAdmin && form.audience_type !== 'all' && !form.audience_filter.trim() && form.audience_type !== 'dept_heads' && form.audience_type !== 'unit_heads') {
    setMsg({ type: 'error', text: 'Please specify the target audience filter.' });
    return;
  }
  setCreateLoading(true);
  try {
    const payload = {
      title: form.title,
      body: form.body,
      is_active: true,
      audience_type: form.audience_type,
      audience_filter: form.audience_filter,
    };
    if (form.expires_at) payload.expires_at = form.expires_at;
    await api.post('/announcements/all/', payload);
    setMsg({ type: 'success', text: 'Announcement sent successfully.' });
    setOpenCreate(false);
    setForm({ title: '', body: '', expires_at: '', audience_type: isDeptHead ? 'department' : 'all', audience_filter: '' });
    fetchAnnouncements();
  } catch {
    setMsg({ type: 'error', text: 'Failed to create announcement.' });
  } finally {
    setCreateLoading(false);
  }
  };

  const handleToggle = async (id, title, isActive) => {
    try {
      await api.post(`/announcements/${id}/toggle/`);
      setMsg({
        type: 'success',
        text: `"${title}" ${isActive ? 'deactivated' : 'activated'}.`
      });
      fetchAnnouncements();
    } catch {
      setMsg({ type: 'error', text: 'Failed to update announcement.' });
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete announcement "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/announcements/${id}/`);
      setMsg({ type: 'success', text: 'Announcement deleted.' });
      fetchAnnouncements();
    } catch {
      setMsg({ type: 'error', text: 'Failed to delete announcement.' });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterStatus('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const hasActiveFilters = search || filterStatus || filterDateFrom || filterDateTo;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      <Sidebar />
      <Box sx={{ ml: isMobile ? 0 : `${SIDEBAR_WIDTH}px`, p: isMobile ? 2 : 3, pt: isMobile ? 7 : 3, minHeight: '100vh', flex: 1, minWidth: 0, overflowX: 'hidden' }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Announcements</Typography>
            <Typography sx={{ color: '#888', fontSize: 13, mt: 0.5 }}>{today}</Typography>
          </Box>
          {(isAdmin || isDeptHead) && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreate(true)}
            sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
          >
            New Announcement
          </Button>
          )}
        </Box>

        {msg.text && (
          <Alert severity={msg.type} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setMsg({ type: '', text: '' })}>
            {msg.text}
          </Alert>
        )}

        {/* Filters */}
        <Card sx={{ mb: 2 }}>
          <Box sx={{ p: 2, background: '#fafcfa', borderBottom: '0.5px solid #e8ede8' }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <TextField
                placeholder="Search announcements..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#aaa' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {isAdmin && <MenuItem value="active">Active</MenuItem>}
                  {isAdmin && <MenuItem value="inactive">Inactive</MenuItem>}
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="From date" type="date" size="small"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="To date" type="date" size="small"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 150 }}
              />
              {hasActiveFilters && (
                <Button
                  size="small"
                  onClick={clearFilters}
                  sx={{ color: '#888' }}
                >
                  Clear filters
                </Button>
              )}
            </Box>
            {hasActiveFilters && (
              <Typography sx={{ fontSize: 12, color: '#888', mt: 1 }}>
                Showing {filtered.length} of {announcements.length} announcements
              </Typography>
            )}
          </Box>

          {/* Table */}
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: '#1a4d2e' }} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Posted by</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Reads</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((a) => (
                  <TableRow
                    key={a.id}
                    sx={{
                      '&:last-child td': { border: 0 },
                      background: !a.is_read_by_me && !isAdmin ? '#fafff8' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!a.is_read_by_me && !isAdmin && (
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#1a4d2e', flexShrink: 0 }} />
                        )}
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 500, cursor: 'pointer', color: '#1a4d2e' }}
                          onClick={() => setOpenView(a)}
                        >
                          {a.title}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#888' }}>{a.created_by_name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      <Chip
                        label={a.audience_type_display || 'All users'}
                        size="small"
                        sx={{ background: '#eaf3de', color: '#27500a', fontSize: 11 }}
                        />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: '#aaa' }}>{a.expires_at || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{a.read_count}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {isAdmin && (
                          <Chip
                            label={a.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            sx={{
                              background: a.is_active ? '#eaf3de' : '#f1efe8',
                              color: a.is_active ? '#27500a' : '#888',
                              fontSize: 11,
                            }}
                          />
                        )}
                        {!isAdmin && (
                          <Chip
                            label={a.is_read_by_me ? 'Read' : 'Unread'}
                            size="small"
                            sx={{
                              background: a.is_read_by_me ? '#f1efe8' : '#eaf3de',
                              color: a.is_read_by_me ? '#888' : '#27500a',
                              fontSize: 11,
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: 11, py: 0.25, borderRadius: 1.5, borderColor: '#1a4d2e', color: '#1a4d2e' }}
                          onClick={() => setOpenView(a)}
                        >
                          View
                        </Button>
                        {isAdmin && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 11, py: 0.25, borderRadius: 1.5, borderColor: '#888', color: '#888' }}
                              onClick={() => handleToggle(a.id, a.title, a.is_active)}
                            >
                              {a.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              sx={{ fontSize: 11, py: 0.25, borderRadius: 1.5 }}
                              onClick={() => handleDelete(a.id, a.title)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#aaa', fontSize: 13 }}>
                      No announcements found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
          )}
        </Card>

        {/* Create dialog */}
        <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CampaignIcon sx={{ color: '#1a4d2e', fontSize: 20 }} />
            New Announcement
            </DialogTitle>
            <DialogContent>
              {/* Audience targeting — admin only */}
                {isAdmin && (
                  <Box sx={{ mb: 2, p: 2, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, mb: 1.5, color: '#1a4d2e' }}>
                      Target audience
                    </Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                      <InputLabel>Send to</InputLabel>
                      <Select
                        label="Send to"
                        value={form.audience_type}
                        onChange={(e) => setForm({ ...form, audience_type: e.target.value, audience_filter: '' })}
                      >
                        <MenuItem value="all">All users</MenuItem>
                        <MenuItem value="dept_heads">All heads of departments</MenuItem>
                        <MenuItem value="unit_heads">All heads of units</MenuItem>
                        <MenuItem value="department">Specific department(s)</MenuItem>
                        <MenuItem value="state">Specific state(s)</MenuItem>
                        <MenuItem value="individuals">Specific individual users</MenuItem>
                      </Select>
                  </FormControl>

              {/* Department selector */}
                  {form.audience_type === 'department' && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Select department(s)</InputLabel>
                      <Select
                        multiple
                        label="Select department(s)"
                        value={form.audience_filter ? form.audience_filter.split(',').map(s => s.trim()) : []}
                        onChange={(e) => setForm({ ...form, audience_filter: e.target.value.join(', ') })}
                        renderValue={(selected) => selected.join(', ')}
                      >
                        {DEPARTMENTS.map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* State selector */}
                  {form.audience_type === 'state' && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Select state(s)</InputLabel>
                      <Select
                      multiple
                      label="Select state(s)"
                      value={form.audience_filter ? form.audience_filter.split(',').map(s => s.trim()) : []}
                      onChange={(e) => setForm({ ...form, audience_filter: e.target.value.join(', ') })}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {STATES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

              {/* Individual user selector */}
        {form.audience_type === 'individuals' && (
          <FormControl fullWidth size="small">
            <InputLabel>Select users</InputLabel>
            <Select
              multiple
              label="Select users"
              value={form.audience_filter ? form.audience_filter.split(',').map(s => s.trim()).filter(Boolean) : []}
              onChange={(e) => setForm({ ...form, audience_filter: e.target.value.join(', ') })}
              renderValue={(selected) => `${selected.length} user(s) selected`}
            >
              {allUsers.map((u) => (
                <MenuItem key={u.id} value={String(u.id)}>
                  {u.first_name} {u.last_name} (@{u.username}) — {u.department}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Summary of targeting */}
        {form.audience_type !== 'all' && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#2d7a4f' }} />
            <Typography sx={{ fontSize: 12, color: '#888' }}>
              {form.audience_type === 'dept_heads' && 'Sending to all department heads'}
              {form.audience_type === 'unit_heads' && 'Sending to all unit heads'}
              {form.audience_type === 'department' && `Sending to: ${form.audience_filter || 'no department selected'}`}
              {form.audience_type === 'state' && `Sending to users in: ${form.audience_filter || 'no state selected'}`}
              {form.audience_type === 'individuals' && `Sending to ${form.audience_filter ? form.audience_filter.split(',').filter(Boolean).length : 0} individual(s)`}
            </Typography>
          </Box>
        )}
      </Box>
    )}

    {/* Dept head audience info */}
    {isDeptHead && (
      <Box sx={{ mb: 2, p: 1.5, background: '#fafcfa', borderRadius: 2, border: '0.5px solid #e0e8e0' }}>
        <Typography sx={{ fontSize: 12, color: '#888' }}>
          This announcement will be sent to all employees in your department: <strong>{user?.department}</strong>
        </Typography>
      </Box>
    )}

    <TextField
      label="Title" fullWidth margin="dense" required
      value={form.title}
      onChange={(e) => setForm({ ...form, title: e.target.value })}
    />
    <TextField
      label="Body" fullWidth margin="dense" required
      multiline rows={6}
      placeholder="Write your announcement here..."
      value={form.body}
      onChange={(e) => setForm({ ...form, body: e.target.value })}
    />
    <TextField
      label="Expiry date (optional)" type="date" fullWidth margin="dense"
      InputLabelProps={{ shrink: true }}
      helperText="Leave blank to keep active indefinitely"
      value={form.expires_at}
      onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
    />
  </DialogContent>
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button onClick={() => setOpenCreate(false)} sx={{ color: '#888' }}>Cancel</Button>
    <Button
      variant="contained"
      onClick={handleCreate}
      disabled={createLoading}
      startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <CampaignIcon />}
      sx={{ background: '#1a4d2e', '&:hover': { background: '#2d7a4f' } }}
    >
      Send announcement
    </Button>
  </DialogActions>
</Dialog>

        {/* View dialog */}
        <Dialog open={!!openView} onClose={() => setOpenView(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
          {openView && (
            <>
              <Box sx={{ background: '#1a4d2e', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CampaignIcon sx={{ color: 'white', fontSize: 20 }} />
                <Typography sx={{ color: 'white', fontWeight: 500 }}>{openView.title}</Typography>
              </Box>
              <DialogContent sx={{ pt: 2.5 }}>
                <Typography sx={{ fontSize: 14, color: '#444', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                  {openView.body}
                </Typography>
                <Box sx={{ mt: 2, pt: 2, borderTop: '0.5px solid #e8ede8' }}>
                  <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                    Posted by {openView.created_by_name} on {new Date(openView.created_at).toLocaleDateString()}
                  </Typography>
                  {openView.expires_at && (
                    <Typography sx={{ fontSize: 12, color: '#aaa' }}>
                      Expires: {openView.expires_at}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 12, color: '#aaa', mt: 0.5 }}>
                    Read by {openView.read_count} user{openView.read_count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setOpenView(null)} sx={{ color: '#888' }}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Box>
  );
}